import type { AppColumn } from '../../../components/AppDataGrid';
import type { QuoteListRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — the ONE column set both lists share.
//
// Open Quotes and Quotes Tracking carry the IDENTICAL nine visible columns in
// legacy: Model.xafml :14104 and :14471 declare the same ColumnInfo set at
// Index 0-7 (they differ only in pixel widths and in their server-side
// criteria). Defining it once means the two screens cannot drift apart — which
// is also standing rule 9.
//
// Legacy order, left to right:
//   Date · Division · QuoteID · Title · Client · Prospect · Amount ·
//   ExpirationDate · Status
// (Division and QuoteID both sit at Index 1 and render in that order.)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Legacy renders Amount with `EditMaskType="Simple" EditMask="c"` — the
 * culture's currency format.
 *
 * ONE DELIBERATE DEVIATION: legacy sets that mask on Open Quotes only
 * (:14145); the Tracking view's Amount column (:14513) has no mask, so the same
 * quote shows as "$100.00" on one screen and the raw `money` value "100.0000"
 * on the other. Both are formatted here. A quoted price is the same number on
 * both screens, and showing it two different ways invites a reader to wonder
 * which one is authoritative.
 *
 * `toLocaleString` on a NUMBER is digit grouping, not a date — explicitly
 * allowed by C2, which bans the locale date methods only.
 */
const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const QUOTE_COLUMNS: AppColumn<QuoteListRow>[] = [
  // Caption "Date" — CRMQuotes.QuotedDate. Date only: legacy declares no time
  // format on the column, and the stamp's time-of-day is when the row happened
  // to be created, which is not something the lab reads off this grid.
  { field: 'quotedDate',     header: 'Date',           width: 110, type: 'date' },
  { field: 'division',       header: 'Division',       width: 170 },
  { field: 'quoteID',        header: 'QuoteID',        width: 130 },
  { field: 'title',          header: 'Title',          width: 160 },
  // The [NonPersistent] NonClient — prospect name or customer name.
  { field: 'client',         header: 'Client',         width: 180 },
  {
    field: 'isProspect', header: 'Prospect', width: 90, align: 'center',
    // A tick box, as legacy shows it - never the word "false".
    render: (r) => <input type="checkbox" checked={r.isProspect} readOnly disabled aria-label="Prospect" />,
  },
  {
    field: 'quotedAmount',
    header: 'Amount',
    width: 110,
    align: 'right',
    // `type: 'number'` would right-align and sort correctly but print the raw
    // money value; the render keeps the currency mask. A null stays blank
    // rather than becoming "$0.00", which would assert a price nobody quoted.
    type: 'number',
    render: (r) => (r.quotedAmount == null ? '' : CURRENCY.format(r.quotedAmount)),
  },
  { field: 'expirationDate', header: 'ExpirationDate', width: 130, type: 'date' },
  // The one column allowed colour (§18.6) — and the label is the lab's own
  // wording, resolved server-side ("Pending Validation", "Won").
  { field: 'status',         header: 'Status',         width: 150, type: 'status' },
];
